import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, Route, Switch } from 'react-router-dom';

import type { IIntegrationUITab } from '@feathr/components';
import { IntegrationPageShell, noop } from '@feathr/components';
import { IntegrationPageFrame } from '@feathr/extender/App/Page';
import { useLocalUrl, useStore } from '@feathr/extender/state';

import RaisersEdgeActivitiesTab from './RaisersEdgeActivitiesTab';
import { getRaisersEdgeConfig } from './raisersEdgeConfig';
import { useRaisersEdgeSave } from './RaisersEdgeIntegrationPageV2.hooks';
import RaisersEdgeSettingsTab from './RaisersEdgeSettingsTab';

/** v1 tab path → v2 tab path, so v1 URLs (bookmarks, help links) keep working. */
const LEGACY_TAB_REDIRECTS: Readonly<Record<string, string>> = {
  overview: 'mapping',
  unsubscribes: 'settings',
};

function RaisersEdgeIntegrationPageV2(): JSX.Element {
  const { t } = useTranslation();
  const localUrl = useLocalUrl();
  const { BlackbaudRaisersEdgeIntegrations } = useStore();

  const integrations = BlackbaudRaisersEdgeIntegrations.list();
  // An account has at most one integration. Check length first: MobX warns on an out-of-bounds read.
  const integration = integrations.models.length ? integrations.models[0] : undefined;
  const config = useMemo(() => getRaisersEdgeConfig(t), [t]);
  const basePath = localUrl('/settings/integrations/raisers-edge');
  const { handleSave, isDirty, isSaving, saveDisabledReason } = useRaisersEdgeSave(integration);

  function renderTab(tab: IIntegrationUITab): ReactNode {
    if (!integration) {
      return null;
    }
    const disabledReason = isSaving ? t('Saving...') : undefined;

    switch (tab.key) {
      case 'activities':
        return (
          <RaisersEdgeActivitiesTab
            disabledReason={disabledReason}
            integration={integration}
            // A replaced model must not keep the old one's state.
            key={integration.id}
            sections={config.toggleSections}
          />
        );

      case 'settings':
        return (
          <RaisersEdgeSettingsTab
            disabledReason={disabledReason}
            integration={integration}
            // A replaced model must not keep the old one's loaded solicit codes.
            key={integration.id}
            sections={config.settingsSections}
          />
        );

      default:
        return null;
    }
  }

  return (
    <Switch>
      {Object.entries(LEGACY_TAB_REDIRECTS).map(([from, to]) => (
        <Redirect exact={true} from={`${basePath}/${from}`} key={from} to={`${basePath}/${to}`} />
      ))}
      <Route>
        <IntegrationPageShell
          basePath={basePath}
          config={config}
          connectionState={integration?.integrationState ?? 'not_connected'}
          importDisabledReason={t('Import is not available yet.')}
          isDirty={isDirty}
          isSaving={isSaving}
          lastSyncedAt={integration?.lastSynced ?? null}
          loading={integrations.isPending}
          // Import and the Mappings and Activities panels are wired up in #9507–#9509.
          onImport={noop}
          onSave={handleSave}
          PageComponent={IntegrationPageFrame}
          renderTab={renderTab}
          saveDisabledReason={saveDisabledReason}
        />
      </Route>
    </Switch>
  );
}

export default observer(RaisersEdgeIntegrationPageV2);
